<template>
  <div ref="appRootRef" class="min-h-screen bg-gray-50">
    <Head />

    <!-- Skip link -->
    <a
      href="#main"
      class="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[60] focus:rounded-lg focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-brand-primary focus:shadow focus:ring-2 focus:ring-teal-500"
    >Skip to content</a>

    <!-- Desktop fixed sidebar (lg+) -->
    <aside
      class="fixed inset-y-0 left-0 z-30 hidden border-r border-white/5 bg-brand-dark transition-[width] duration-200 ease-out motion-reduce:transition-none lg:flex lg:flex-col"
      :class="collapsed ? 'lg:w-[72px]' : 'lg:w-64'"
    >
      <SidebarNav :collapsed="collapsed" variant="rail" @toggle-collapse="toggleCollapse" />
    </aside>

    <!-- Mobile drawer -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-200 ease-out motion-reduce:transition-none"
        enter-from-class="opacity-0" enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150 ease-in motion-reduce:transition-none"
        leave-from-class="opacity-100" leave-to-class="opacity-0"
      >
        <div v-if="drawerOpen" class="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden" aria-hidden="true" @click="closeDrawer"></div>
      </Transition>

      <Transition
        enter-active-class="transition-transform duration-300 ease-out motion-reduce:transition-none"
        enter-from-class="-translate-x-full" enter-to-class="translate-x-0"
        leave-active-class="transition-transform duration-200 ease-in motion-reduce:transition-none"
        leave-from-class="translate-x-0" leave-to-class="-translate-x-full"
      >
        <div
          v-if="drawerOpen"
          id="mobile-nav"
          ref="drawerPanelRef"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          class="fixed inset-y-0 left-0 z-50 flex w-[84vw] max-w-[300px] flex-col bg-brand-dark shadow-2xl lg:hidden"
        >
          <div class="flex h-16 shrink-0 items-center justify-between border-b border-white/5 px-4">
            <span class="flex items-center gap-2.5">
              <span class="grid h-8 w-8 place-items-center rounded-lg bg-brand-gradient text-sm font-bold text-white shadow-sm shadow-black/20">J</span>
              <span class="text-[15px] font-semibold tracking-tight text-white">Support Panel</span>
            </span>
            <button
              type="button"
              data-drawer-close
              @click="closeDrawer"
              aria-label="Close navigation menu"
              class="inline-flex h-11 w-11 items-center justify-center rounded-lg text-white/70 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300"
            >
              <NavIcon name="x-mark" class="h-6 w-6" />
            </button>
          </div>
          <SidebarNav :collapsed="false" variant="drawer" class="min-h-0 flex-1" />
        </div>
      </Transition>
    </Teleport>

    <!-- Content column -->
    <div
      class="flex min-h-screen flex-col transition-[padding] duration-200 ease-out motion-reduce:transition-none"
      :class="collapsed ? 'lg:pl-[72px]' : 'lg:pl-64'"
    >
      <!-- Top bar. Note: backdrop-blur establishes a stacking context, so the nested user-menu
           dropdown's z-index is local to this header. The dropdown is right-aligned and the sidebar
           is left, so they never overlap; no global z-index escape is needed. -->
      <header class="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-gray-200 bg-white/90 px-4 backdrop-blur supports-[backdrop-filter]:bg-white/75 sm:px-6">
        <!-- Hamburger (mobile) -->
        <button
          type="button"
          ref="hamburgerRef"
          @click="openDrawer"
          class="-ml-2 inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 lg:hidden"
          aria-label="Open navigation menu"
          :aria-expanded="drawerOpen"
        >
          <NavIcon name="bars-3" class="h-6 w-6" />
        </button>

        <!-- Title / breadcrumb -->
        <div class="min-w-0 flex-1">
          <h1 class="truncate text-sm font-semibold text-gray-800 sm:text-base">
            <span class="hidden font-normal text-gray-400 sm:inline">Support / </span>{{ pageTitle }}
          </h1>
        </div>

        <!-- User menu -->
        <div class="relative" ref="menuRef">
          <button
            type="button"
            ref="menuTriggerRef"
            @click="menuOpen = !menuOpen"
            class="inline-flex h-11 items-center gap-2 rounded-full pl-1 pr-1 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 sm:pr-2"
            aria-haspopup="menu"
            :aria-expanded="menuOpen"
            :aria-label="'Account: ' + (auth?.user?.email || '')"
          >
            <span aria-hidden="true" class="grid h-8 w-8 place-items-center rounded-full bg-brand-gradient text-xs font-semibold text-white ring-1 ring-black/5">{{ initials }}</span>
            <span class="hidden max-w-[180px] truncate text-sm text-gray-600 md:block">{{ auth?.user?.email }}</span>
            <NavIcon name="chevron-down" class="hidden h-4 w-4 text-gray-400 sm:block" />
          </button>

          <Transition
            enter-active-class="transition ease-out duration-100" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="menuOpen"
              id="user-menu"
              role="menu"
              class="absolute right-0 z-40 mt-2 w-60 origin-top-right rounded-xl border border-gray-200 bg-white py-1.5 shadow-lg ring-1 ring-black/5"
            >
              <div role="none" class="border-b border-gray-100 px-3.5 py-2.5">
                <p class="truncate text-sm font-medium text-gray-900">{{ auth?.user?.email }}</p>
                <p class="mt-0.5 text-xs text-gray-500">{{ roleLabel }}</p>
              </div>
              <Link
                :href="route('logout')"
                method="post"
                as="button"
                role="menuitem"
                @click="closeMenu"
                class="mx-1 mt-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 focus:outline-none focus-visible:bg-red-50"
              >
                <NavIcon name="arrow-right-on-rectangle" class="h-5 w-5" />
                Logout
              </Link>
            </div>
          </Transition>
        </div>
      </header>

      <!-- Global Notification Bar -->
      <GlobalNotification :message="page.props.globalNotification" />

      <!-- Main content -->
      <main id="main" tabindex="-1" class="flex-1 focus:outline-none">
        <div class="mx-auto w-full max-w-[1536px] px-4 py-6 sm:px-6 lg:px-8">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { Head, Link, usePage } from '@inertiajs/vue3';
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue';
import GlobalNotification from './Partials/GlobalNotification.vue';
import SidebarNav from './Partials/SidebarNav.vue';
import NavIcon from './Partials/NavIcon.vue';
import { useSupportNav } from './useSupportNav.js';

const props = defineProps({
  // Pages may override the top-bar title; otherwise it derives from the active nav item.
  title: { type: String, default: '' },
});

const page = usePage();
const { navGroups, auth, isAdmin, isOwner, isActive } = useSupportNav();

/* ----- Top-bar title ----- */
const activeItem = computed(() => {
  for (const group of navGroups) {
    const match = group.items.find((i) => isActive(i.routeName));
    if (match) return match;
  }
  return null;
});
const pageTitle = computed(() => props.title || activeItem.value?.label || 'Support Panel');

/* ----- User identity ----- */
const initials = computed(() => {
  const email = auth.value?.user?.email || '';
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (local.slice(0, 2) || '?').toUpperCase();
});
const roleLabel = computed(() => (isOwner.value ? 'Owner' : isAdmin.value ? 'Admin' : 'Support'));

/* ----- Desktop sidebar collapse (persisted) ----- */
const collapsed = ref(false); // SSR-safe default: expanded
const toggleCollapse = () => { collapsed.value = !collapsed.value; };
watch(collapsed, (v) => {
  if (typeof window !== 'undefined') window.localStorage.setItem('sp.sidebar.collapsed', v ? '1' : '0');
});

/* ----- Mobile drawer ----- */
const appRootRef = ref(null);
const drawerOpen = ref(false);
const hamburgerRef = ref(null);
const drawerPanelRef = ref(null);
// When the drawer closes via an explicit dismiss (Escape / scrim / close button) we restore focus
// to the hamburger that opened it. When it closes as a side effect of navigation, we DON'T —
// focus should follow the user to the new page, not snap back to the menu toggle.
let restoreFocusOnClose = true;
const openDrawer = () => { drawerOpen.value = true; };
const closeDrawer = () => { drawerOpen.value = false; };

/* ----- User menu ----- */
const menuOpen = ref(false);
const menuRef = ref(null);
const menuTriggerRef = ref(null);
const closeMenu = () => { menuOpen.value = false; };

const onDocClick = (e) => {
  if (menuOpen.value && menuRef.value && !menuRef.value.contains(e.target)) closeMenu();
};
const onKeydown = (e) => {
  if (e.key !== 'Escape') return;
  if (menuOpen.value) { closeMenu(); menuTriggerRef.value?.focus(); }
  else if (drawerOpen.value) { closeDrawer(); }
};

// Crossing up to the lg breakpoint hides the drawer (it's lg:hidden); close it so we don't leave
// the app root inert / body scroll-locked behind a now-invisible panel.
let lgMql = null;
const onLgChange = (e) => { if (e.matches && drawerOpen.value) closeDrawer(); };

// Close transient UI on navigation. The drawer closes here as a side effect, so suppress the
// focus-restore-to-hamburger (let focus follow to the new page instead).
watch(() => page.url, () => {
  closeMenu();
  if (drawerOpen.value) {
    restoreFocusOnClose = false;
    closeDrawer();
  }
});

onMounted(() => {
  // Restore persisted collapse state (client only).
  collapsed.value = window.localStorage.getItem('sp.sidebar.collapsed') === '1';

  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKeydown);

  lgMql = window.matchMedia('(min-width: 1024px)');
  lgMql.addEventListener('change', onLgChange);

  // Drawer: scroll-lock, modal focus trap, and focus management.
  watch(drawerOpen, (open) => {
    document.documentElement.classList.toggle('overflow-hidden', open);
    // The drawer is teleported to <body>, so inerting the app root makes everything behind the
    // scrim non-focusable and aria-hidden — enforcing the aria-modal contract and trapping Tab
    // within the drawer without bespoke key handling.
    if (appRootRef.value) appRootRef.value.inert = open;
    if (open) {
      nextTick(() => drawerPanelRef.value?.querySelector('[data-drawer-close]')?.focus());
    } else if (restoreFocusOnClose) {
      hamburgerRef.value?.focus();
    }
    restoreFocusOnClose = true;
  });
});

onUnmounted(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onKeydown);
  lgMql?.removeEventListener('change', onLgChange);
  if (typeof document !== 'undefined') document.documentElement.classList.remove('overflow-hidden');
  if (appRootRef.value) appRootRef.value.inert = false;
});
</script>
